import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CloudUploadIcon, DownloadIcon, FileTextIcon, FolderIcon, FolderLockIcon, FolderPlusIcon, HeartPulseIcon, LayoutGridIcon, ListIcon, LockIcon, PenLineIcon, RocketIcon, SearchIcon, UploadIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { docFolders, recentFiles } from '../../data/documents';
import { useHRStore } from '../../hooks/useHRStore';
import type { RecentFile } from '../../types/hr';
import type { IconComponent } from '../../types/ui';
import { TODAY, formatDate } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { Modal } from '../ui/Modal';
interface DocumentsTabProps {
  employeeId: string;
  autoOpenUpload?: boolean;
  onAutoOpenHandled?: () => void;
}
const FOLDER_ICONS: Record<string, {
  icon: IconComponent;
  tint: string;
}> = {
  uploads: {
    icon: FolderIcon,
    tint: 'bg-azure/10 text-azure'
  },
  hr: {
    icon: FolderLockIcon,
    tint: 'bg-navy/[0.07] text-navy'
  },
  insurance: {
    icon: HeartPulseIcon,
    tint: 'bg-rose-50 text-rose-500'
  },
  signed: {
    icon: PenLineIcon,
    tint: 'bg-flame/10 text-flame'
  },
  onboarding: {
    icon: RocketIcon,
    tint: 'bg-deep/10 text-deep'
  }
};
const DEFAULT_ICON = {
  icon: FolderIcon,
  tint: 'bg-slate-100 text-slate-500'
};
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
export function DocumentsTab({
  employeeId,
  autoOpenUpload,
  onAutoOpenHandled
}: DocumentsTabProps) {
  const store = useHRStore();
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [query, setQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [pending, setPending] = useState<{
    name: string;
    size: string;
  }[]>([]);
  const [uploadFolder, setUploadFolder] = useState('Employee Uploads');
  const [folderName, setFolderName] = useState('');
  useEffect(() => {
    if (autoOpenUpload) {
      setUploadOpen(true);
      onAutoOpenHandled?.();
    }
  }, [autoOpenUpload, onAutoOpenHandled]);
  const uploaded = store.files[employeeId] ?? [];
  const allFiles: RecentFile[] = [...uploaded, ...recentFiles];
  const allFolders = [...docFolders, ...(store.folders[employeeId] ?? [])].map(f => ({
    ...f,
    count: f.count + uploaded.filter(u => u.folder === f.name).length
  }));
  const q = query.trim().toLowerCase();
  const folders = allFolders.filter(f => !q || f.name.toLowerCase().includes(q));
  const files = allFiles.filter(f => (!activeFolder || f.folder === activeFolder) && (!q || `${f.name} ${f.folder}`.toLowerCase().includes(q)));
  const commitFiles = (items: {
    name: string;
    size: string;
  }[], folder: string) => {
    const today = format(TODAY, 'yyyy-MM-dd');
    hrActions.addFiles(employeeId, items.map((item, i) => ({
      id: `up-${Date.now()}-${i}`,
      name: item.name,
      folder,
      size: item.size,
      date: today,
      by: 'Jordan Avery'
    })));
    toast.success(`${items.length} file${items.length === 1 ? '' : 's'} uploaded`, {
      description: `Saved to ${folder}.`
    });
  };
  const openUpload = () => {
    setPending([]);
    setUploadFolder('Employee Uploads');
    setUploadOpen(true);
  };
  const confirmUpload = () => {
    if (!pending.length) return;
    commitFiles(pending, uploadFolder);
    setUploadOpen(false);
    setPending([]);
  };
  const createFolder = () => {
    const name = folderName.trim();
    if (!name) return;
    if (allFolders.some(f => f.name.toLowerCase() === name.toLowerCase())) {
      toast.error('A folder with that name already exists');
      return;
    }
    hrActions.addFolder(employeeId, {
      id: `folder-${Date.now()}`,
      name,
      count: 0,
      description: 'Created just now'
    });
    toast.success('Folder created', {
      description: name
    });
    setFolderName('');
    setFolderOpen(false);
  };
  return <div className="space-y-6" data-id="element-140" data-mp="2rw8iciukn5ec">
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-soft ring-1 ring-slate-900/[0.04] md:flex-row md:items-center" data-id="element-141" data-mp="2vvhcvpxfr7ay">
        <div className="flex gap-2" data-id="element-142" data-mp="3jmmry2vjxvk7">
          <Button variant="primary" icon={UploadIcon} onClick={openUpload} data-id="element-143" data-mp="3jno2m68nog1e">
            Upload
          </Button>
          <Button icon={FolderPlusIcon} onClick={() => setFolderOpen(true)} data-id="element-144" data-mp="3at9my2k7r3hr">
            New folder
          </Button>
        </div>
        <div className="relative flex-1 md:ml-auto md:max-w-xs" data-id="element-145" data-mp="3etjtphftxxxo">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-146" data-mp="6f1s9yke9gn1" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search documents" aria-label="Search documents" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-147" data-mp="b6bf6rek65v8" />
        </div>
        <div className="flex rounded-xl bg-canvas p-1 ring-1 ring-inset ring-slate-200" role="group" aria-label="Folder view" data-id="element-148" data-mp="1cx2wwhlogpx0">
          {([{
          id: 'grid',
          icon: LayoutGridIcon,
          label: 'Grid view'
        }, {
          id: 'list',
          icon: ListIcon,
          label: 'List view'
        }] as const).map(({
          id,
          icon: Icon,
          label
        }) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} aria-label={label} className={`flex h-8 w-8 items-center justify-center rounded-lg transition ${view === id ? 'bg-white text-navy shadow-sm' : 'text-muted hover:text-navy'}`} data-id="element-149" data-mp="1huulb1n45twg">
              <Icon className="h-4 w-4" data-id="element-150" data-mp="35cknwoq8r099" />
            </button>)}
        </div>
      </div>

      {folders.length > 0 && <div className={view === 'grid' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5' : 'divide-y divide-slate-100 rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]'} data-id="element-151" data-mp="3adgr102rqidm">
          {folders.map(f => {
        const {
          icon: Icon,
          tint
        } = FOLDER_ICONS[f.id] ?? DEFAULT_ICON;
        const selected = activeFolder === f.name;
        const toggle = () => setActiveFolder(selected ? null : f.name);
        return view === 'grid' ? <button key={f.id} type="button" onClick={toggle} aria-pressed={selected} className={`group rounded-2xl bg-white p-5 text-left shadow-soft ring-1 transition hover:-translate-y-0.5 hover:shadow-lift ${selected ? 'ring-2 ring-azure' : 'ring-slate-900/[0.04]'}`} data-id="element-152" data-mp="2we8lzqjxcg1i">
                <div className="flex items-start justify-between" data-id="element-153" data-mp="h0ye8uyqr2rc">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tint}`} data-id="element-154" data-mp="c0k5fapbfos9">
                    <Icon className="h-5 w-5" data-id="element-155" data-mp="71zek1vyehgh" />
                  </span>
                  {f.locked && <LockIcon className="h-4 w-4 text-mutedlight" aria-label="Locked" data-id="element-156" data-mp="22mhypfa5w0p" />}
                  {f.badge && <Badge tone="orange" data-id="element-157" data-mp="3thbsaehk5jv2">{f.badge}</Badge>}
                </div>
                <p className="mt-4 text-sm font-semibold text-navy group-hover:text-azure" data-id="element-158" data-mp="3tg0le5stfvzq">{f.name}</p>
                <p className="mt-0.5 text-xs text-muted" data-id="element-159" data-mp="3pdm6fws9pgdq">
                  {f.count} files · {f.description}
                </p>
              </button> : <button key={f.id} type="button" onClick={toggle} aria-pressed={selected} className={`flex w-full items-center gap-4 px-5 py-3.5 text-left transition hover:bg-canvas/70 ${selected ? 'bg-azure/[0.05]' : ''}`} data-id="element-160" data-mp="3fg7r60i2yxn0">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tint}`} data-id="element-161" data-mp="1781iwoygf51a">
                  <Icon className="h-4 w-4" data-id="element-162" data-mp="t8tijsqk1qvi" />
                </span>
                <span className="flex-1 text-sm font-medium text-navy" data-id="element-163" data-mp="y73ew6o0mxip">{f.name}</span>
                {f.locked && <Badge tone="navy" icon={LockIcon} data-id="element-164" data-mp="jhp4cv8pea1q">
                    HR only
                  </Badge>}
                {f.badge && <Badge tone="orange" data-id="element-165" data-mp="aik5tox56gpk">{f.badge}</Badge>}
                <span className="w-16 text-right text-xs text-muted" data-id="element-166" data-mp="ajv6vgz7dacz">{f.count} files</span>
              </button>;
      })}
        </div>}

      <div onDragOver={e => {
      e.preventDefault();
      setDragOver(true);
    }} onDragLeave={() => setDragOver(false)} onDrop={e => {
      e.preventDefault();
      setDragOver(false);
      const dropped = Array.from(e.dataTransfer.files).map(f => ({
        name: f.name,
        size: formatSize(f.size)
      }));
      if (dropped.length) commitFiles(dropped, 'Employee Uploads');
    }} className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${dragOver ? 'border-azure bg-azure/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-167" data-mp="knyz9b8m7x9">
        <span className={`flex h-12 w-12 items-center justify-center rounded-full ${dragOver ? 'bg-azure text-white' : 'bg-azure/10 text-azure'}`} data-id="element-168" data-mp="4u6w5mvxmxak">
          <CloudUploadIcon className="h-6 w-6" data-id="element-169" data-mp="elbaqlhp4g4v" />
        </span>
        <p className="mt-3 text-sm font-medium text-navy" data-id="element-170" data-mp="9njj7skyqw17">Drag & drop files here</p>
        <p className="mt-1 text-xs text-muted" data-id="element-171" data-mp="9pm829adqj4c">
          or{' '}
          <button type="button" onClick={openUpload} className="font-medium text-azure hover:underline" data-id="element-172" data-mp="ol24gkyfmr3w">
            browse your computer
          </button>{' '}
          · PDF, DOCX, JPG up to 25 MB
        </p>
      </div>

      <Card title={activeFolder ? `Files in ${activeFolder}` : 'Recent files'} icon={FileTextIcon} bodyClassName="pb-2" action={activeFolder && <Button variant="ghost" size="sm" icon={XIcon} onClick={() => setActiveFolder(null)} data-id="element-174" data-mp="iqh136hqaopq">
              Show all
            </Button>} data-id="element-173" data-mp="skfwlmnara8p">
        {files.length === 0 ? <div className="px-6 pb-4" data-id="element-175" data-mp="1bf4p39tv3s6z">
            <EmptyState icon={SearchIcon} title={activeFolder ? 'This folder is empty' : `No documents match “${query}”`} compact data-id="element-176" data-mp="16hmi3ozenn1m" />
          </div> : <div className="overflow-x-auto" data-id="element-177" data-mp="12inb7yu45g7j">
            <table className="w-full min-w-[600px] text-left text-sm" data-id="element-178" data-mp="xi92sfolo48z">
              <thead data-id="element-179" data-mp="3n6rxgrs2bt50">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-180" data-mp="3n71p0jsra8ct">
                  <th scope="col" className="px-6 py-2.5" data-id="element-181" data-mp="3i63y9itok5zp">Name</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-182" data-mp="1xqfl3lffgne4">Folder</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-183" data-mp="22oquxw5q2xwc">Added</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-184" data-mp="22oz5viiabt4w">By</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-185" data-mp="26p9opvr6ee16">
                    <span className="sr-only" data-id="element-186" data-mp="2bgss9lkhvy2e">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-187" data-mp="2ggx95cpbeut5">
                {files.map(f => {
              const ext = (f.name.split('.').pop() ?? 'file').slice(0, 4).toUpperCase();
              return <tr key={f.id} className="group transition-colors hover:bg-azure/[0.03]" data-id="element-188" data-mp="2qfmqn4daud8v">
                      <td className="px-6 py-3" data-id="element-189" data-mp="2uelwqsarog1w">
                        <div className="flex items-center gap-3" data-id="element-190" data-mp="w0ai631mho9r">
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-[9px] font-bold text-rose-500" data-id="element-191" data-mp="w23p7jg5eaia">{ext}</span>
                          <div data-id="element-192" data-mp="23c9xcwcasqdt">
                            <p className="font-medium text-navy" data-id="element-193" data-mp="23dbhrrf7ep87">{f.name}</p>
                            <p className="text-xs text-muted" data-id="element-194" data-mp="1yeqh5qw1i3j8">{f.size}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-slate-600" data-id="element-195" data-mp="28bw7uy0qrsix">{f.folder}</td>
                      <td className="whitespace-nowrap px-6 py-3 text-slate-600" data-id="element-196" data-mp="28dz7m9rp500u">{formatDate(f.date)}</td>
                      <td className="px-6 py-3" data-id="element-197" data-mp="2m9krk2j2otme">
                        {f.by === 'DocuSign' ? <Badge tone="orange" icon={PenLineIcon} data-id="element-198" data-mp="2haghzas8ktvj">
                            DocuSign
                          </Badge> : <span className="text-slate-600" data-id="element-199" data-mp="2r3p3z1b3xtuv">{f.by}</span>}
                      </td>
                      <td className="px-6 py-3 text-right" data-id="element-200" data-mp="3502ji4w2zl6p">
                        <button type="button" onClick={() => toast.success('Download started', {
                    description: f.name
                  })} aria-label={`Download ${f.name}`} className="rounded-lg p-1.5 text-muted opacity-0 transition hover:bg-azure/10 hover:text-azure focus:opacity-100 group-hover:opacity-100" data-id="element-201" data-mp="3043mjswosuuo">
                          <DownloadIcon className="h-4 w-4" data-id="element-202" data-mp="3eo6hm3zch8n2" />
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </Card>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload documents" description="Files are stored on this employee's record." footer={<>
            <Button onClick={() => setUploadOpen(false)} data-id="element-204" data-mp="2i0jnwdtno4db">Cancel</Button>
            <Button variant="primary" icon={UploadIcon} disabled={!pending.length} onClick={confirmUpload} data-id="element-205" data-mp="2mz3a8fc5vi67">
              Upload {pending.length ? `(${pending.length})` : ''}
            </Button>
          </>} data-id="element-203" data-mp="2r0orsdzn4kmq">
        <div className="space-y-4" data-id="element-206" data-mp="29zu62awt7okl">
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 px-4 py-8 text-center transition hover:border-azure hover:bg-azure/[0.04]" data-id="element-207" data-mp="2a0vs1afd5vh3">
            <CloudUploadIcon className="h-7 w-7 text-azure" data-id="element-208" data-mp="214o45n0ntqnc" />
            <span className="mt-2 text-sm font-medium text-navy" data-id="element-209" data-mp="1w8z3thgtg5pz">Choose files</span>
            <span className="text-xs text-muted" data-id="element-210" data-mp="1r79p9rudx9x5">PDF, DOCX, JPG up to 25 MB</span>
            <input type="file" multiple className="sr-only" onChange={e => {
            const chosen = Array.from(e.target.files ?? []).map(f => ({
              name: f.name,
              size: formatSize(f.size)
            }));
            setPending(prev => [...prev, ...chosen]);
            e.target.value = '';
          }} data-id="element-211" data-mp="1m8os61afj6cx" />
          </label>
          {pending.length > 0 && <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-100" data-id="element-212" data-mp="ydd6y5lntqaa">
              {pending.map((p, i) => <li key={`${p.name}-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-id="element-213" data-mp="183y5jt1c2ut7" data-mpi={i}>
                  <FileTextIcon className="h-4 w-4 text-muted" data-id="element-214" data-mp="1c51lsgnamsuy" />
                  <span className="flex-1 truncate text-navy" data-id="element-215" data-mp="1c4i5139dqaii">{p.name}</span>
                  <span className="text-xs text-muted" data-id="element-216" data-mp="fgc0shgddk4r">{p.size}</span>
                  <button type="button" aria-label={`Remove ${p.name}`} onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="rounded p-1 text-muted hover:text-rose-600" data-id="element-217" data-mp="kdlp1ts0vpr0">
                    <XIcon className="h-3.5 w-3.5" data-id="element-218" data-mp="tfbhn2xn4uqd" />
                  </button>
                </li>)}
            </ul>}
          <label className="block text-xs font-medium text-slate-600" data-id="element-219" data-mp="1zuch3ilvjv1p">
            Save to folder
            <select value={uploadFolder} onChange={e => setUploadFolder(e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-220" data-mp="24sdylz3njmb2">
              {allFolders.map(f => <option key={f.id} data-id="element-221" data-mp="9qajs1m73oey">{f.name}</option>)}
            </select>
          </label>
        </div>
      </Modal>

      <Modal open={folderOpen} onClose={() => setFolderOpen(false)} title="New folder" footer={<>
            <Button onClick={() => setFolderOpen(false)} data-id="element-223" data-mp="3rwf31zoh531b">Cancel</Button>
            <Button variant="primary" icon={FolderPlusIcon} disabled={!folderName.trim()} onClick={createFolder} data-id="element-224" data-mp="3rxq9665e6mvl">
              Create folder
            </Button>
          </>} data-id="element-222" data-mp="3i7dl43ydcbkp">
        <label className="block text-xs font-medium text-slate-600" data-id="element-225" data-mp="2ze9rgil5hyq3">
          Folder name
          <input autoFocus value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') createFolder();
        }} placeholder="e.g. Performance Reviews" className="mt-1.5 h-11 w-full rounded-xl border-0 bg-white px-3.5 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-226" data-mp="2ughyrmcom8ku" />
        </label>
      </Modal>
    </div>;
}